import type { AppState } from '../../state/appState.js';
import type { ComponentDef, Scene, SceneNodeId } from '../../scene/sceneState.js';
import type { LayerDef } from '../../scene/layerState.js';
import { cloneComponentDef, cloneLayerDef } from './clone.js';
import { findSceneParentId, getSceneTargetFor, removeChildId } from './sceneHelpers.js';

function allSceneTargetsFor(appState: AppState) {
  return [
    getSceneTargetFor(appState, null),
    ...Object.values(appState.componentLibrary).map(component => getSceneTargetFor(appState, component.id)),
  ];
}

export function componentHasInstanceUsersFor(
  appState: AppState,
  componentId: string,
  ignoredNodeIds: ReadonlySet<SceneNodeId> = new Set(),
): boolean {
  for (const target of allSceneTargetsFor(appState)) {
    if (Object.values(target.scene.nodes).some(node =>
      node.kind === 'instance'
      && node.componentId === componentId
      && !ignoredNodeIds.has(node.id),
    )) {
      return true;
    }
  }
  return false;
}

export function deleteComponentOwnedLayerDefsFor(appState: AppState, componentId: string): boolean {
  const component = appState.componentLibrary[componentId];
  if (!component) return false;

  for (const node of Object.values(component.scene.nodes)) {
    if (node.kind === 'layer') delete appState.layerLibrary[node.layerId];
  }
  delete appState.componentLibrary[componentId];
  return true;
}

export function deleteComponentIfUnreferencedFor(
  appState: AppState,
  componentId: string,
  ignoredNodeIds: ReadonlySet<SceneNodeId> = new Set(),
): boolean {
  if (componentHasInstanceUsersFor(appState, componentId, ignoredNodeIds)) return false;
  return deleteComponentOwnedLayerDefsFor(appState, componentId);
}

export function collectRemovedForSubtreeFor(appState: AppState, subtreeNodes: Scene['nodes']): {
  components: Record<string, ComponentDef>;
  layerDefs: Record<string, LayerDef>;
} {
  const subtreeIds = new Set(Object.keys(subtreeNodes));
  const components: Record<string, ComponentDef> = {};
  const layerDefs: Record<string, LayerDef> = {};

  for (const node of Object.values(subtreeNodes)) {
    if (node.kind === 'instance') {
      const componentId = node.componentId;
      if (componentHasInstanceUsersFor(appState, componentId, subtreeIds) || components[componentId]) continue;
      const component = appState.componentLibrary[componentId];
      if (!component) continue;

      components[componentId] = cloneComponentDef(component);
      for (const componentNode of Object.values(component.scene.nodes)) {
        if (componentNode.kind !== 'layer') continue;
        const layerDef = appState.layerLibrary[componentNode.layerId];
        if (layerDef) layerDefs[componentNode.layerId] = cloneLayerDef(layerDef);
      }
    } else if (node.kind === 'layer') {
      const layerDef = appState.layerLibrary[node.layerId];
      if (layerDef) layerDefs[node.layerId] = cloneLayerDef(layerDef);
    }
  }

  return { components, layerDefs };
}

export function deleteSceneSubtreeFor(
  appState: AppState,
  scene: Scene,
  rootId: SceneNodeId,
  subtreeNodes: Scene['nodes'],
): void {
  const subtreeIds = new Set(Object.keys(subtreeNodes));

  function removeDescendants(id: SceneNodeId): void {
    const node = scene.nodes[id];
    if (!node) return;
    if (node.kind === 'frame') {
      for (const childId of [...node.childIds]) removeDescendants(childId);
    } else if (node.kind === 'instance') {
      deleteComponentIfUnreferencedFor(appState, node.componentId, subtreeIds);
    } else if (node.kind === 'layer') {
      delete appState.layerLibrary[node.layerId];
    }
    delete scene.nodes[id];
  }

  removeChildId(scene, findSceneParentId(scene, rootId), rootId);
  removeDescendants(rootId);
}
